import { TOKEN_EXPIRY_LOCAL_STORAGE_KEY } from "@/api/_client";
import { softLogOut } from "@/api/auth";
import { useUser } from "@/api/user";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { ButtonLoading } from "./ui/button-loading";
import { Dialog, DialogContent } from "./ui/dialog";

const MIN_TIME_TO_SHOW_DIALOG = 60; // seconds

export const SessionTimeControl = () => {
  const [expiryTime, setExpiryTime] = React.useState<number | null>(null);
  const [currentTime, setCurrentTime] = React.useState<number>(
    new Date().getTime(),
  );
  const user = useUser(true);
  const [isLoading, setIsLoading] = React.useState(false);

  const queryClient = useQueryClient();

  const checkTokenExpiry = React.useCallback(() => {
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_LOCAL_STORAGE_KEY);
    if (tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry);
      setExpiryTime(expiryTime);
    }
  }, []);

  React.useEffect(() => {
    const tokenCheckInterval = setInterval(() => {
      checkTokenExpiry();
    }, 5 * 1000);

    return () => clearInterval(tokenCheckInterval);
  }, [checkTokenExpiry]);

  React.useEffect(() => {
    const timeUpdateInterval = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);

    return () => clearInterval(timeUpdateInterval);
  }, []);

  const getTimeLeftInSeconds = React.useCallback(() => {
    if (!user.data) return null;
    if (!expiryTime) return null;
    const timeLeftMs = expiryTime - currentTime;
    if (timeLeftMs <= 0) {
      queryClient.setQueryData(["currentUser"], null);
      softLogOut();
      queryClient.resetQueries();
      return 0;
    } else if (timeLeftMs > MIN_TIME_TO_SHOW_DIALOG * 1000) {
      setIsLoading(false);
    }
    return Math.max(0, Math.floor(timeLeftMs / 1000));
  }, [expiryTime, currentTime, user.data, queryClient]);

  const timeLeftSeconds = React.useMemo(() => {
    return getTimeLeftInSeconds();
  }, [getTimeLeftInSeconds]);

  if (
    timeLeftSeconds === null ||
    timeLeftSeconds <= 0 ||
    timeLeftSeconds > MIN_TIME_TO_SHOW_DIALOG
  )
    return null;

  return (
    <Dialog open={true}>
      <DialogContent showCloseButton={false} className="max-w-sm">
        <div className="w-full text-center text-xl">
          You will be logged out in
        </div>
        <div className="w-full text-center text-8xl">{timeLeftSeconds}</div>
        <div className="w-full text-center text-xl">
          seconds due to inactivity
        </div>
        <ButtonLoading
          isLoading={isLoading}
          size="lg"
          onClick={() => {
            setIsLoading(true);
            queryClient.invalidateQueries({
              queryKey: ["currentUser"],
            });
            setTimeout(() => {
              checkTokenExpiry();
            }, 1000);
          }}
        >
          Stay Logged In
        </ButtonLoading>
      </DialogContent>
    </Dialog>
  );
};
